/**
 * Qual idioma o jogo abre, e onde a escolha fica guardada.
 *
 * Três fontes, nesta ordem: o que a pessoa escolheu neste aparelho, o idioma do
 * navegador e, se nada disso servir, o português. A escolha vem primeiro porque
 * ela é a única das três que alguém disse em voz alta; palpite bom não desfaz
 * decisão tomada.
 *
 * A conta é uma quarta fonte, mas ela não mora aqui: quem junta a escolha deste
 * aparelho com o `locale` que o servidor guarda é `src/ui/SeletorDeIdioma.tsx`,
 * que é quem tem a sessão na mão. Este arquivo não fala com a rede.
 */
import { IDIOMAS, IDIOMA_PADRAO, type Idioma } from "./nucleo";

/** Mesma convenção das outras chaves do jogo (`lm:push:token`, por exemplo). */
const CHAVE = "lm:idioma";

/**
 * O idioma de uma etiqueta BCP 47, ou nada quando não houver equivalente.
 *
 * Serve tanto para `navigator.language` quanto para o `locale` que o servidor
 * devolve, que é a mesma coisa em formato diferente. A comparação é pela
 * primeira parte da etiqueta porque `pt-PT`, `en-GB` e `es-419` são o idioma
 * que o jogo tem: recusar por causa da região deixaria a pessoa em português
 * sem motivo.
 */
export function idiomaDaEtiqueta(etiqueta: string | null | undefined): Idioma | null {
  if (!etiqueta) return null;
  const limpa = etiqueta.trim().toLowerCase();
  if (limpa.length === 0) return null;
  const base = limpa.split("-")[0];
  return (
    IDIOMAS.find((idioma) => idioma.toLowerCase() === limpa) ??
    IDIOMAS.find((idioma) => idioma.toLowerCase().split("-")[0] === base) ??
    null
  );
}

/**
 * Idioma que o navegador anuncia.
 *
 * `languages` vem antes de `language` porque é a lista inteira de preferência:
 * quem configurou galego e depois espanhol merece o espanhol, não o português
 * de quem nunca pediu nada.
 */
export function idiomaDoNavegador(): Idioma {
  if (typeof navigator === "undefined") return IDIOMA_PADRAO;
  const etiquetas = navigator.languages ?? [navigator.language];
  for (const etiqueta of etiquetas) {
    const achado = idiomaDaEtiqueta(etiqueta);
    if (achado) return achado;
  }
  return IDIOMA_PADRAO;
}

/*
 * Leitura e escrita em try/catch, como em `online/push.ts`: janela anônima,
 * dados do site bloqueados e WebView com armazenamento desligado jogam nas duas
 * pontas. O jogo continua inteiro sem isso, só esquece a escolha ao fechar.
 */

/** A escolha guardada neste aparelho, se houver uma que o jogo ainda conheça. */
export function idiomaGuardado(): Idioma | null {
  try {
    return idiomaDaEtiqueta(localStorage.getItem(CHAVE));
  } catch {
    return null;
  }
}

export function guardarIdioma(idioma: Idioma): void {
  try {
    localStorage.setItem(CHAVE, idioma);
  } catch {
    // Sem lugar para guardar. A escolha vale até fechar a aba, e pronto.
  }
}

/** O idioma com que o jogo abre. É o que o provedor usa quando ninguém manda. */
export function idiomaInicial(): Idioma {
  return idiomaGuardado() ?? idiomaDoNavegador();
}
